import React, { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import {
  emptyPayslipCompanyForm,
  readPayslipCompanyForTab,
  savePayslipCompany,
} from "../../../app/utils/finance/payslipCompanyDetails";
import { compressSignatureImageFile } from "../../../app/utils/common/compressImageFile";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";

const SalarySlipCompanyDetailsModal = ({
  open,
  onClose,
  companyKey = "",
  companyLabel = "",
  onSaved,
}) => {
  const [form, setForm] = useState(emptyPayslipCompanyForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const loadContextRef = useRef({ key: "", label: "" });

  useEffect(() => {
    if (!open) return;

    loadContextRef.current = {
      key: companyKey,
      label: companyLabel,
    };

    setErrors({});
    setLoading(true);
    readPayslipCompanyForTab(companyKey, companyLabel)
      .then(setForm)
      .catch(() =>
        setForm({
          ...emptyPayslipCompanyForm(),
          companyName: companyLabel || "",
        })
      )
      .finally(() => setLoading(false));
  }, [open]);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const onSignatureChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      updateField("signatureFileName", "");
      updateField("signatureDataUrl", "");
      return;
    }

    const allowed =
      ["image/jpeg", "image/jpg", "image/png"].includes(file.type) ||
      /\.(jpe?g|png)$/i.test(file.name);
    if (!allowed) {
      setErrors((current) => ({
        ...current,
        signatureFileName: "Use a JPG or PNG file.",
      }));
      event.target.value = "";
      return;
    }

    setSignatureLoading(true);
    try {
      const { dataUrl, fileName } = await compressSignatureImageFile(file);
      setForm((current) => ({
        ...current,
        signatureFileName: fileName,
        signatureDataUrl: dataUrl,
      }));
      setErrors((current) => ({ ...current, signatureFileName: "" }));
    } catch {
      showErrorToast("Could not process signature image. Try a smaller JPG or PNG.");
      event.target.value = "";
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!String(form.companyName || "").trim()) {
      nextErrors.companyName = "Company name is required.";
    }
    if (!String(form.ownerName || "").trim()) {
      nextErrors.ownerName = "Owner / signatory name is required.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      showErrorToast(Object.values(nextErrors)[0]);
      return;
    }

    setSaving(true);
    try {
      const { key } = loadContextRef.current;
      const saved = await savePayslipCompany(form, { companyKey: key });
      setForm(saved);
      showSuccessToast(
        `Payslip details saved for ${saved.companyName || companyLabel || "company"}.`
      );
      onSaved?.();
      onClose();
    } catch (error) {
      showErrorToast(
        error?.response?.data?.error?.message || error?.message || "Failed to save payslip details."
      );
    } finally {
      setSaving(false);
    }
  };

  const displayCompany = companyLabel || form.companyName || "this company";
  const inputClass = "w-full border border-[#e8ecf3] rounded-lg px-3 py-2 text-sm text-[#27314a] bg-white";

  return (
    <Modal
      title={`Payslip details — ${displayCompany}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      destroyOnClose
    >
      <p className="text-sm text-[#6f7d97] mb-4">
        Shown on the salary slip PDF footer for <strong>{displayCompany}</strong>. Contact and address
        are managed under Finance Management → Add company.
      </p>
      {loading ? (
        <p className="text-sm text-[#6f7d97] py-6 text-center">Loading payslip details…</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
              Company name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`${inputClass} ${errors.companyName ? "border-red-400" : ""}`}
              placeholder="The Coding Studio"
              value={form.companyName}
              onChange={(e) => updateField("companyName", e.target.value)}
              disabled={loading || saving}
            />
            {errors.companyName ? (
              <p className="text-xs text-red-500 mt-1">{errors.companyName}</p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
              Owner / signatory name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`${inputClass} ${errors.ownerName ? "border-red-400" : ""}`}
              placeholder="Himanshu G."
              value={form.ownerName}
              onChange={(e) => updateField("ownerName", e.target.value)}
              disabled={loading || saving}
            />
            {errors.ownerName ? (
              <p className="text-xs text-red-500 mt-1">{errors.ownerName}</p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Signature image</label>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              className="w-full text-sm"
              onChange={onSignatureChange}
              disabled={loading || saving || signatureLoading}
            />
            <p className="text-xs text-[#9aa8c0] mt-2 leading-relaxed">
              Optional. Upload <strong>PNG or JPG</strong>
            </p>
            {signatureLoading ? (
              <p className="text-xs text-[#6f7d97] mt-1">Compressing image…</p>
            ) : null}
            {errors.signatureFileName ? (
              <p className="text-xs text-red-500 mt-1">{errors.signatureFileName}</p>
            ) : null}
            {form.signatureFileName ? (
              <p className="text-xs text-[#6f7d97] mt-1">{form.signatureFileName}</p>
            ) : null}
            {form.signatureDataUrl ? (
              <img
                src={form.signatureDataUrl}
                alt="Signature preview"
                className="mt-3 max-h-20 object-contain border border-[#e8ecf3] rounded p-2 bg-white"
              />
            ) : null}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-[#e8ecf3] text-sm text-[#5f6d8a]"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button bg-btn-primary text-white px-5 py-2 rounded-lg text-sm"
              disabled={saving || signatureLoading}
            >
              {saving ? "Saving…" : "Save details"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default SalarySlipCompanyDetailsModal;
